import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import ExploreSection from "../../components/ProductDetails/ExploreSection";
import ServiceCard from "../../components/Common/ServiceCard";

const AIDrivenCybersecurityServicesPage = () => {
  // Key Benefits data
  const keyBenefitsData = [
    {
      id: 1,
      title: "Reduced Response Time",
      description:
        "Detect and mitigate threats within seconds using AI automation.",
      icon: "/svg/24_7.svg",
      iconColor: "#000B8C",
      bgColor: "#E6F3FF",
    },
    {
      id: 2,
      title: "Predict Unknown Attacks",
      description:
        "Identify and neutralize zero-day vulnerabilities before they strike.",
      icon: "/svg/Cybersecurity.svg",
      iconColor: "#000B8C",
      bgColor: "#E6F3FF",
    },
    {
      id: 3,
      title: "Enhanced Visibility",
      description:
        "Get a unified, real-time view of your entire digital infrastructure.",
      icon: "/svg/analytics.svg",
      iconColor: "#000B8C",
      bgColor: "#E6F3FF",
    },
    {
      id: 4,
      title: "Lower Operational Costs",
      description:
        "Automate monitoring and response, reducing manual overhead.",
      icon: "/svg/smart.svg",
      iconColor: "#000B8C",
      bgColor: "#E6F3FF",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>AI-Driven Cybersecurity Services | Intelligent Protection for Government & Enterprise in Bangladesh</title>
        <meta 
          name="description" 
          content="Secure your systems with AI-driven cybersecurity services from Business Automation Ltd.—Bangladesh's trusted partner in e-Governance, donor-funded platforms, and enterprise digital protection." 
        />
        <meta 
          name="keywords" 
          content="AI cybersecurity services Bangladesh, cybersecurity for government systems, endpoint protection Bangladesh, AI-powered threat detection, DevSecOps Bangladesh, cloud security Bangladesh, SOC as a service Bangladesh, donor-compliant IT security Bangladesh, cyber resilience Bangladesh, identity and access management Bangladesh, cybersecurity for OSS platforms, AI in public sector security" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/ai-driven-cybersecurity-services" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/ai-driven-cybersecurity-services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI-Driven Cybersecurity Services | Intelligent Protection for Government & Enterprise in Bangladesh" />
        <meta property="og:description" content="Secure your systems with AI-driven cybersecurity services from Business Automation Ltd.—Bangladesh's trusted partner in e-Governance, donor-funded platforms, and enterprise digital protection." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/ai-driven-cybersecurity-services" />
        <meta name="twitter:title" content="AI-Driven Cybersecurity Services | Intelligent Protection for Government & Enterprise in Bangladesh" />
        <meta name="twitter:description" content="Secure your systems with AI-driven cybersecurity services from Business Automation Ltd.—Bangladesh's trusted partner in e-Governance, donor-funded platforms, and enterprise digital protection." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="AI-Driven Cybersecurity Services"
        subtitle="Advanced AI-Powered Security Solutions"
        backgroundImage="/png/bg_aids.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          { label: "AI-Driven Cybersecurity Services", href: "#", isCurrentPage: true },
        ]}
      />

      <Box py={{ base: "30px", md: "40px" }}>
        <Container maxW="7xl">
          <VStack spacing={0} align="start" w="full">
            {/* Introduction Section */}
            <VStack spacing={0} align="start" w="full">
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                lineHeight="1.2"
                textAlign="justify"
              >
                As digital transformation accelerates across governments,
                enterprises, and donor-backed initiatives, cybersecurity is no
                longer optional—it's mission-critical. At{" "}
                <Text as="span" fontWeight="bold" color="gray.800">
                  Business Automation Limited
                </Text>
                , we provide{" "}
                <Text as="span" fontWeight="bold" color="gray.800">
                  AI-driven cybersecurity services
                </Text>{" "}
                that go beyond traditional defense, offering{" "}
                <Text as="span" fontWeight="bold" color="gray.800">
                  intelligent threat prediction, rapid incident response, and
                  real-time protection
                </Text>{" "}
                for critical systems.
              </Text>
              <Box h={6} />
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                lineHeight="1.2"
                textAlign="justify"
              >
                Our solutions are designed for today's complex digital
                environments—
                <Text as="span" fontWeight="bold" color="gray.800">
                  cloud-native, mobile-first, and data-rich
                </Text>
                —protecting everything from national portals to enterprise
                infrastructure.
              </Text>
            </VStack>

            {/* What We Offer Section */}
            <Box w="full" py={8}>
              <VStack spacing={2} align="start" w="full">
                <Heading
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="gray.800"
                >
                  Why{" "}
                  <Text as="span" color="#000B8C">
                    AI-Driven Cybersecurity?
                  </Text>
                </Heading>

                <VStack spacing={0} align="start" w="full">
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="gray.600"
                    lineHeight="1.2"
                    textAlign="justify"
                  >
                    Traditional security tools react after breaches.{" "}
                    <Text as="span" fontWeight="bold" color="gray.800">
                      AI-driven security solutions act proactively.
                    </Text>
                  </Text>

                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="gray.600"
                    lineHeight="1.2"
                    textAlign="justify"
                  >
                    We leverage artificial intelligence, machine learning, and
                    behavioral analytics to detect anomalies, predict threats,
                    and automate responses before damage occurs.
                  </Text>
                </VStack>
              </VStack>
            </Box>
            <Heading
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color="gray.800"
            >
              <Text as="span" color="#000B8C">
                Our Cybersecurity{" "}
              </Text>
              Capabilities
            </Heading>
            <Box w="full" display="flex" justifyContent="center" py={8}>
              <Box
                as="img"
                src="/png/cybersecurity.png"
                alt="AI-driven cybersecurity capabilities"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                draggable="false"
              />
            </Box>
            <Heading
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              pt={8}
              color="gray.800"
            >
              <Text as="span" color="#000B8C">
                How Our AI-Driven Cybersecurity Works
              </Text>
            </Heading>
            <Box w="full" display="flex" justifyContent="center" pt={8}>
              <Box
                as="img"
                src="/png/aidriven_info.png"
                alt="AI-driven cybersecurity capabilities"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                draggable="false"
              />
            </Box>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              py={4}
              lineHeight="1.2"
              textAlign="justify"
            >
              Our AI-powered security framework continuously learns from network
              patterns and global threat intelligence. It predicts potential
              attacks, detects anomalies in real-time, responds automatically to
              contain breaches, and prevents future risks through adaptive
              analytics.
            </Text>
            {/* Key Benefits at a Glance Section */}
            <Box w="full" py={8}>
              <VStack spacing={6} align="start" w="full">
                <Heading
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="gray.800"
                  pt={4}
                >
                  <Text as="span" color="#000B8C">
                    Key Benefits{" "}
                  </Text>
                  at a Glance
                </Heading>

                <SimpleGrid
                  columns={{ base: 1, md: 2, lg: 4 }}
                  spacing={4}
                  w="full"
                >
                  {keyBenefitsData.map((benefit) => (
                    <ServiceCard key={benefit.id} service={benefit} />
                  ))}
                </SimpleGrid>
              </VStack>
            </Box>
            <Heading
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color="gray.800"
              pt={8}
            >
              <Text as="span" color="#000B8C">
                Our Technology Stack
              </Text>
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              py={4}
              lineHeight="1.2"
              textAlign="justify"
            >
              Our cybersecurity platform integrates advanced machine learning,
              big data analytics, and global threat intelligence engines with
              trusted Security Information and Event Management (SIEM)
              solutions.
            </Text>
            <Box w="full" display="flex" justifyContent="center" pt={8}>
              <Box
                as="img"
                src="/png/aiTechStack.png"
                alt="Technology Stack"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                draggable="false"
              />
            </Box>
            
          </VStack>
          <Text
              fontSize={{ base: "lg", md: "2xl" }}
              color="#000B8C"
              py={8}
              fontWeight={"semibold"}
              fontStyle={"italic"}
              lineHeight="1.2"
              align={"center"}
              textAlign="center"
            >
              Securing Bangladesh’s Digital Future — with Intelligent, Adaptive
              Cyber Defense by Business Automation Limited
            </Text>
          <ExploreSection />
        </Container>
      </Box>
    </>
  );
};

export default AIDrivenCybersecurityServicesPage;
